import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { parseAbsolute, ZonedDateTime } from '@internationalized/date';
import {
  getHistogramBarIntervalForRange,
  HistogramDataPoint,
} from '@antimatterhq/ui/components/histogram';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
} from '@/atoms/query/queryAtoms';
import { useGetQueriesHistogram } from '@/hooks/dbui/useGetQueryHistogram';
import { filterNulls } from '@/utils/filterNulls';
import { queryStartValueAtomFamily } from '@/atoms/query/queryResultsAtoms';

export const convertToInterval = (milliseconds: number) => {
  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30.4 * day; // Approximate month
  const year = 365.25 * day; // Approximate year

  const units = [
    { name: 'year', value: year },
    { name: 'month', value: month },
    { name: 'week', value: week },
    { name: 'day', value: day },
    { name: 'hour', value: hour },
    { name: 'minute', value: minute },
    { name: 'second', value: second },
  ];

  const parts: string[] = [];
  let remaining = milliseconds;

  for (const unit of units) {
    const count = Math.floor(remaining / unit.value);
    if (count >= 1) {
      const unitName = count === 1 ? unit.name : `${unit.name}s`;
      parts.push(`${count} ${unitName}`);
      remaining = remaining % unit.value;

      // Only show the highest unit and optionally the next unit if there's a remainder
      if (parts.length === 1 && remaining > 0) {
        // Continue to get the next unit
        continue;
      } else {
        // We have our result
        break;
      }
    }
  }

  return parts.join(' ') || '0 seconds';
};

// I wrote this to fetch more data but realized this isn't correct.
// This will call the new "extend" endpoint to fetch more data and then
// at the new time ranges to fetch data for
export const useQueryHistogram = (tabId: string) => {
  const tab = useAtomValue(queryTabAtomFamily(tabId));

  const previousQueriedDataRangeRef = useRef<{
    start: string;
    end: string;
  } | null>(null);

  const onLoadData = useCallback(
    (startTime: ZonedDateTime, endTime: ZonedDateTime) => {
      if (!tab) {
        throw new Error(
          `Unable to add time range to histogram for tab ${tabId} not found`,
        );
      }

      // eslint-disable-next-line no-console
      console.log('TODO: extend the time range', startTime, endTime);

      //   const timeRanges = tab.histogram.timeRanges;

      //   if (
      //     timeRanges.some(
      //       (range) =>
      //         range.start.toString() === startTime.toString() &&
      //         range.end.toString() === endTime.toString(),
      //     )
      //   ) {
      //     return;
      //   }

      //   timeRanges.push({ start: startTime, end: endTime });

      //   updateTab({
      //     histogram: {
      //       timeRanges,
      //       interval: tab.histogram.interval,
      //     },
      //   });
    },
    [tab, tabId],
  );

  const onTimeRangeChange = useAtomCallback(
    useCallback(
      (
        _get,
        set,
        _start: ZonedDateTime,
        _end: ZonedDateTime,
        intervalInMs: number,
      ) => {
        if (!tab) {
          throw new Error(
            `Unable to change time range for tab ${tabId} not found`,
          );
        }

        const interval = convertToInterval(intervalInMs);
        if (interval === tab?.histogram.interval) {
          return;
        }

        // The time range passed into this function is the new visible time range.
        // We don't actually care about this and still want to use the queriedDataRanges
        set(updateTabAtomFamily(tabId), {
          histogram: {
            timeRanges: tab.histogram.timeRanges,
            interval,
          },
        });
      },
      [tab, tabId],
    ),
  );

  const onTimeRangeSelection = useAtomCallback(
    useCallback(
      (
        get,
        set,
        startTime: ZonedDateTime,
        _endTime: ZonedDateTime,
        intervalInMs: number,
      ) => {
        const startTimestamp = startTime.toDate();
        const startValueISO = startTimestamp.toISOString();
        const currentTab = get(queryTabAtomFamily(tabId));

        const interval = convertToInterval(intervalInMs);

        set(updateTabAtomFamily(tabId), {
          histogram: {
            timeRanges: currentTab?.histogram.timeRanges ?? [],
            interval,
          },
        });

        set(queryStartValueAtomFamily(tabId), startValueISO);
      },
      [tabId],
    ),
  );

  const updateHistogramTimeRanges = useAtomCallback(
    useCallback(
      (get, set) => {
        const currentTab = get(queryTabAtomFamily(tabId));
        if (currentTab?.queriedDataRange) {
          const interval = getHistogramBarIntervalForRange(
            currentTab.queriedDataRange.start,
            currentTab.queriedDataRange.end,
          );

          set(updateTabAtomFamily(tabId), {
            histogram: {
              timeRanges: [
                {
                  start: currentTab.queriedDataRange.start.copy(),
                  end: currentTab.queriedDataRange.end.copy(),
                },
              ],
              interval: convertToInterval(interval),
            },
          });
        }
      },
      [tabId],
    ),
  );

  useEffect(() => {
    if (tab?.queriedDataRange) {
      const currentRange = {
        start: tab.queriedDataRange.start.toAbsoluteString(),
        end: tab.queriedDataRange.end.toAbsoluteString(),
      };

      // Check if the queriedDataRange has changed
      const hasChanged =
        !previousQueriedDataRangeRef.current ||
        previousQueriedDataRangeRef.current.start !== currentRange.start ||
        previousQueriedDataRangeRef.current.end !== currentRange.end;

      if (hasChanged) {
        // Update the ref with the new value
        previousQueriedDataRangeRef.current = currentRange;

        // Update histogram time ranges
        updateHistogramTimeRanges();
      }
    }
  }, [tab?.queriedDataRange, updateHistogramTimeRanges]);

  const results = useGetQueriesHistogram(
    tab?.histogram.timeRanges.map((range) => ({
      queryId: tab.queryId || '',
      startDate: range.start.toAbsoluteString(),
      endDate: range.end.toAbsoluteString(),
      interval: tab.histogram.interval || '',
      disabled: tab.status !== 'success',
    })) || [],
  );

  const dataPoints: HistogramDataPoint[] = useMemo(() => {
    return filterNulls(results.map((result) => result.data?.data))
      .flat()
      .map((point) => ({
        startTime: parseAbsolute(point.start, tab!.timezone),
        endTime: parseAbsolute(point.end, tab!.timezone),
        value: point.count,
      }));
  }, [results, tab]);

  const loadedTimeRanges: [ZonedDateTime, ZonedDateTime][] = useMemo(() => {
    return (
      tab?.histogram.timeRanges
        .filter((_, i) => !results[i].isPending)
        .map((range) => [
          parseAbsolute(range.start.toAbsoluteString(), tab!.timezone),
          parseAbsolute(range.end.toAbsoluteString(), tab!.timezone),
        ]) || []
    );
  }, [tab, results]);

  // TODO: We don't support multi loading states for the histogram so we'll
  // need to think of something when we are using the query extend function to fetch more data
  const isPending = useMemo(() => {
    return results.length && results[0].isPending;
  }, [results]);

  return {
    onLoadData,
    dataPoints,
    isPending,
    loadedTimeRanges,
    onTimeRangeChange,
    onTimeRangeSelection,
  };
};
